/**
 * Een branch samenvoegen in z'n ouder — `docs/architecture/branch-merge.md`
 * §3.10–§3.11.
 *
 * 1. De proefrun: wat erbij komt, verandert en verdwijnt; welke branches een
 *    andere ouder krijgen; wat andere branches overnemen (M9).
 * 2. Conflicten kiezen. Standaard wint de branch (M7); per veld kan de
 *    gebruiker de ouder kiezen. Elke keuze rekent de proefrun opnieuw uit.
 * 3. Samenvoegen: eerst de aankondiging aan wie in het workpackage werkt
 *    (`announceFirst.ts`), dan de merge zelf. De server maakt vooraf een
 *    herstelpunt; terugzetten daarvan draait de merge terug.
 */

import type { TFunction } from "i18next";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ApiError,
  api,
  type MergePreview,
  type MergeSide,
  type OperationPreview,
} from "../api/client";
import { resolveBilingual } from "../registry";
import { announceThenWait, finishOperation } from "../state/announceFirst";

interface Props {
  workspaceId: string;
  branch: { id: string; name: string };
  onClose: () => void;
  onMerged: (result: MergePreview) => void;
}

type Lang = "nl" | "en";

/** Een naam of waarde leesbaar maken: tweetalig in de UI-taal, de rest kort. */
function show(value: unknown, lang: Lang): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "object" && !Array.isArray(value)) {
    const v = value as Record<string, unknown>;
    if ("nl" in v || "en" in v) return resolveBilingual(v as { nl?: string; en?: string }, lang);
  }
  if (typeof value === "boolean") return value ? "✓" : "—";
  const text = typeof value === "string" ? value : JSON.stringify(value);
  return text.length > 80 ? `${text.slice(0, 77)}…` : text;
}

/**
 * Een veldpad uit de proefrun leesbaar maken: `name_json.nl` → "Naam (NL)",
 * `attributes_json.eigenaar` → "Attribuut eigenaar", `stereotypes_json[x]`
 * → "Stereotype x".
 */
function fieldLabel(path: string, t: TFunction): string {
  const member = /^(\w+)\[(.+)\]$/.exec(path);
  if (member) return t(`merge.field.${member[1]}`, { key: member[2] });
  const [column, ...rest] = path.split(".");
  const key = rest.join(".");
  const label = t(`merge.field.${column}`, { key });
  if (!key) return label;
  return /^(nl|en)$/.test(key) ? `${label} (${key.toUpperCase()})` : label;
}

/** De proefrun in de vorm die de aankondiging bij collega's laat zien. */
function asOperationPreview(p: MergePreview, lang: Lang): OperationPreview {
  const named = (kind: string) =>
    p.changes.filter((c) => c.kind === kind).map((c) => ({ id: c.id, name: show(c.name, lang) }));
  return {
    kind: "merge",
    created: {
      concepts: p.counts.concept.added,
      relations: p.counts.relation.added,
      diagrams: p.counts.diagram.added,
      folders: 0,
    },
    overwritten: { concepts: named("concept"), relations: named("relation") },
    overwritten_counts: {
      concepts: p.counts.concept.changed,
      relations: p.counts.relation.changed,
    },
    removed: { concepts: [], relations: [] },
    removed_counts: { concepts: p.counts.concept.deleted, relations: p.counts.relation.deleted },
    diagrams: p.changes
      .filter((c) => c.kind === "diagram")
      .map((c) => ({
        id: c.id,
        name: show(c.name, lang),
        action: "update" as const,
        placements: 0,
      })),
    reused: 0,
    truncated: p.changes_truncated,
  };
}

export function MergeBranchDialog({ workspaceId, branch, onClose, onMerged }: Props) {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === "en" ? "en" : "nl";
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [choices, setChoices] = useState<Record<string, MergeSide>>({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(
    async (next: Record<string, MergeSide>) => {
      setLoading(true);
      setError(null);
      try {
        setPreview(await api.previewMergeBranch(workspaceId, branch.id, next));
      } catch (err) {
        const body = err instanceof ApiError ? (err.body as { message?: string } | null) : null;
        setError(body?.message ?? (err instanceof Error ? err.message : String(err)));
      } finally {
        setLoading(false);
      }
    },
    [workspaceId, branch.id],
  );

  useEffect(() => {
    void load({});
  }, [load]);

  const choose = (key: string, side: MergeSide) => {
    const next = { ...choices, [key]: side };
    setChoices(next);
    void load(next);
  };

  const run = async () => {
    if (!preview) return;
    setBusy(true);
    setError(null);
    try {
      const label = t("merge.operationLabel", {
        source: preview.source.name,
        target: preview.target.name,
      });
      const op = await announceThenWait(workspaceId, branch.id, {
        kind: "merge",
        label,
        impact: {
          nieuw: preview.counts.concept.added + preview.counts.relation.added,
          gewijzigd: preview.counts.concept.changed + preview.counts.relation.changed,
          verdwijnt: preview.counts.concept.deleted + preview.counts.relation.deleted,
          diagrammen: preview.counts.diagram.added + preview.counts.diagram.changed,
        },
        preview: asOperationPreview(preview, lang),
        // Het akkoord is hier in de dialoog al gegeven.
        askPreview: async () => true,
      });
      if (!op.go) {
        setError(t("merge.cancelled"));
        return;
      }
      let result: MergePreview;
      try {
        result = await api.mergeBranch(workspaceId, branch.id, choices);
      } finally {
        await finishOperation(workspaceId, branch.id, op.operationId);
      }
      onMerged(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const kinds = ["concept", "relation", "diagram"] as const;
  const names = preview?.branch_names ?? {};

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: presentationele overlay-laag; sluiten via de knoppen, klik-buiten is muis-gemak
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
      role="presentation"
    >
      <div
        className="ledger flex max-h-[90vh] w-full max-w-2xl flex-col gap-4 overflow-y-auto rounded-xl border border-(--ld-control) bg-(--ld-surface) p-5 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="merge-title"
      >
        <h2 id="merge-title" className="font-semibold text-base text-(--ld-text)">
          {preview
            ? t("merge.title", { source: preview.source.name, target: preview.target.name })
            : t("merge.titleLoading", { source: branch.name })}
        </h2>

        {loading && !preview && <p className="text-(--ld-muted) text-sm">{t("merge.loading")}</p>}
        {error && (
          <p className="rounded border border-(--ld-control) bg-(--ld-negative-bg) px-3 py-2 text-(--ld-negative-ink) text-sm">
            {error}
          </p>
        )}

        {preview && (
          <>
            <p className="text-(--ld-text) text-sm">
              {t("merge.intro", { source: preview.source.name, target: preview.target.name })}
            </p>

            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-(--ld-muted) text-xs">
                  <th className="py-1 pr-3" />
                  <th className="py-1 pr-3">{t("merge.added")}</th>
                  <th className="py-1 pr-3">{t("merge.changed")}</th>
                  <th className="py-1 pr-3">{t("merge.deleted")}</th>
                </tr>
              </thead>
              <tbody>
                {kinds.map((k) => (
                  <tr key={k} className="border-(--ld-rule) border-t">
                    <td className="py-1 pr-3 text-(--ld-text)">{t(`merge.kind.${k}`)}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{preview.counts[k].added}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {preview.counts[k].changed}
                    </td>
                    <td className="py-1 pr-3 text-right tabular-nums">
                      {preview.counts[k].deleted}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {preview.children.length > 0 && (
              <p className="text-(--ld-text) text-sm">
                {t("merge.children", {
                  target: preview.target.name,
                  list: preview.children.map((c) => c.name).join(", "),
                })}
              </p>
            )}

            <section className="flex flex-col gap-2">
              <h3 className="font-medium text-(--ld-text) text-sm">{t("merge.conflictsTitle")}</h3>
              {preview.conflicts.length === 0 && (
                <p className="text-(--ld-muted) text-sm">{t("merge.noConflicts")}</p>
              )}
              {preview.conflicts.map((c) => (
                <div
                  key={`${c.kind}:${c.logicalId}:${c.type}`}
                  className="rounded border border-transparent bg-(--ld-notice-bg) px-3 py-2 text-sm"
                >
                  <div className="font-medium text-(--ld-text)">
                    {show(c.name, lang)}{" "}
                    <span className="font-normal text-(--ld-muted) text-xs">
                      {t(`merge.kind.${c.kind}`)}
                    </span>
                  </div>
                  {c.type === "fields" &&
                    (c.fields ?? []).map((f) => {
                      const key = `${c.kind}:${c.logicalId}:${f.path}`;
                      return (
                        <fieldset key={key} className="mt-1 flex flex-col gap-0.5">
                          <legend className="text-(--ld-muted) text-xs" title={f.path}>
                            {fieldLabel(f.path, t)}
                          </legend>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={key}
                              checked={f.resolution === "source"}
                              onChange={() => choose(key, "source")}
                              disabled={busy || loading}
                            />
                            {preview.source.name}: {show(f.source, lang)}
                          </label>
                          <label className="flex items-center gap-2">
                            <input
                              type="radio"
                              name={key}
                              checked={f.resolution === "target"}
                              onChange={() => choose(key, "target")}
                              disabled={busy || loading}
                            />
                            {preview.target.name}: {show(f.target, lang)}
                          </label>
                        </fieldset>
                      );
                    })}
                  {c.type !== "fields" && (
                    <fieldset className="mt-1 flex flex-col gap-0.5">
                      <legend className="text-(--ld-muted) text-xs">
                        {t(`merge.conflict.${c.type}`, {
                          source: preview.source.name,
                          target: preview.target.name,
                        })}
                      </legend>
                      {(["source", "target"] as const).map((side) => (
                        <label key={side} className="flex items-center gap-2">
                          <input
                            type="radio"
                            name={`${c.kind}:${c.logicalId}`}
                            checked={(c.resolution ?? "source") === side}
                            onChange={() => choose(`${c.kind}:${c.logicalId}`, side)}
                            disabled={busy || loading}
                          />
                          {t(`merge.conflict.${c.type}_${side}`)}
                        </label>
                      ))}
                    </fieldset>
                  )}
                </div>
              ))}
            </section>

            {preview.propagation.length > 0 && (
              <section className="flex flex-col gap-1">
                <h3 className="font-medium text-(--ld-text) text-sm">
                  {t("merge.propagationTitle")}
                </h3>
                <ul className="list-disc pl-5 text-(--ld-text) text-sm">
                  {preview.propagation.map((p) => (
                    <li key={`${p.branchId}:${p.kind}:${p.logicalId}`}>
                      {t(`merge.propagation.${p.status}`, {
                        branch: names[p.branchId] ?? p.branchId,
                        name: show(p.name, lang),
                        taken: p.taken.length,
                        kept: p.kept.length,
                      })}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {preview.duplicates.length > 0 && (
              <p className="text-(--ld-muted) text-xs">
                {t("merge.duplicates", { count: preview.duplicates.length })}
              </p>
            )}

            <p className="text-(--ld-muted) text-xs">{t("merge.safetyNote")}</p>
          </>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1.5 text-(--ld-text) text-sm hover:bg-(--ld-raised) disabled:opacity-50"
          >
            {t("merge.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void run()}
            disabled={busy || loading || !preview}
            className="rounded bg-(--ld-primary) px-3 py-1.5 text-sm text-(--ld-primary-ink) hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
          >
            {busy ? t("merge.running") : t("merge.confirm")}
          </button>
        </div>
      </div>
    </div>
  );
}
